var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { initialize } from 'launchdarkly-js-client-sdk';
import { config } from 'lib-shared-backend-config';
import { defaultFeatureFlags, } from 'lib-shared-feature-flags';
import { getHighestUserRole } from 'lib-shared-user-role';
import { kebabToCamelCase } from 'utils/formatting/string';
/**
 * Represents the feature flags setup.
 *
 * This encapsulates the logic and communication with LaunchDarkly as well as
 * feature flag evaluation and tracking.
 */
class FeatureFlags {
    constructor() {
        /**
         * The evaluated flags.
         *
         * We keep track of this to avoid spamming LaunchDarkly with identical
         * evaluations of the same flag. Essentially we only evaluate once per
         * session
         *
         * - I.e. until the agent refreshes the page.
         */
        this.evaluated = new Set();
        /** The LaunchDarkly client. */
        this.client = null;
        /** The feature flags. */
        this.flags = null;
    }
    /**
     * (Re)Initializes the LaunchDarkly client.
     *
     * @param user The user.
     * @param organization The organization.
     */
    init(user, organization) {
        return __awaiter(this, void 0, void 0, function* () {
            this.deleteUnusedFlagsFromLocalStorage();
            return new Promise((resolve) => {
                if (this.client) {
                    this.client.close().then(() => {
                        this.setupClient(user, organization);
                        this.setupClientEventListeners({ validate: false, resolve });
                    });
                }
                else {
                    this.setupClient(user, organization);
                    this.setupClientEventListeners({ validate: true, resolve });
                }
            });
        });
    }
    /**
     * Gets the feature flags.
     *
     * @param names The names.
     */
    get(names) {
        if (!this.flags) {
            throw Error(`The feature flags cannot be used before they are initialized, ${names.join(', ')}`);
        }
        this.track(names);
        return names.reduce((acc, name) => {
            var _a;
            return (Object.assign(Object.assign({}, acc), { [kebabToCamelCase(name)]: (_a = this.flags) === null || _a === void 0 ? void 0 : _a[name] }));
        }, {});
    }
    /**
     * Tracks the variations.
     *
     * @param names The names.
     */
    track(names) {
        names
            .filter((name) => !this.evaluated.has(name))
            .forEach((name) => {
            if (this.client) {
                this.client.variation(name);
                this.evaluated.add(name);
            }
        });
    }
    /**
     * Sets up the LaunchDarkly client.
     *
     * @param user The user.
     * @param organization The organization.
     */
    setupClient(user, organization) {
        var _a;
        this.client = initialize(config.sdk.launchDarkly, {
            key: user.id,
            email: (_a = user.email) !== null && _a !== void 0 ? _a : undefined,
            name: `${organization.name}: ${user.is_admin ? 'admin' : 'agent'} user`,
            custom: {
                type: [...user.roles],
                role: getHighestUserRole(user.roles),
                organizationID: organization.id,
                organizationDomain: organization.subdomain,
                trialEndedAt: (organization === null || organization === void 0 ? void 0 : organization.trial_ended_at)
                    ? organization === null || organization === void 0 ? void 0 : organization.trial_ended_at
                    : '',
                isInTrial: typeof organization.trial_ended_at === 'string' ? false : true,
                appVersion: VERSION,
                appBuildTime: TIMESTAMP,
                hostname: window.location.hostname,
            },
        }, {
            bootstrap: 'localStorage',
            privateAttributeNames: ['email'],
            sendEvents: true,
            sendEventsOnlyForVariation: true,
        });
    }
    /*
     * Clear feature flags saved in local storage for old builds.
     */
    deleteUnusedFlagsFromLocalStorage() {
        Object.keys(localStorage)
            .filter((key) => /ld:\w+:(?!\$)\w+/.test(key))
            .forEach((key) => {
            var _a, _b;
            const [_ld, token, userEncodedB64] = key.split(':');
            const user = JSON.parse(atob(userEncodedB64));
            if (token === config.sdk.launchDarkly &&
                (((_a = user === null || user === void 0 ? void 0 : user.custom) === null || _a === void 0 ? void 0 : _a.appVersion) !== VERSION ||
                    ((_b = user === null || user === void 0 ? void 0 : user.custom) === null || _b === void 0 ? void 0 : _b.appBuildTime) !== TIMESTAMP)) {
                localStorage.removeItem(key);
            }
        });
    }
    /**
     * Sets up the LaunchDarkly event listeners.
     *
     * @param options The listener options.
     */
    setupClientEventListeners(options) {
        if (this.client) {
            // The ready is event will always trigger regardless of whether it
            // successfully connected to LaunchDarkly.
            this.client.on('ready', () => {
                var _a, _b;
                if (options.validate) {
                    this.validateFlags();
                }
                this.flags = Object.assign(Object.assign({}, defaultFeatureFlags), (_a = this.client) === null || _a === void 0 ? void 0 : _a.allFlags());
                options.resolve({ flags: this.flags });
                (_b = this.client) === null || _b === void 0 ? void 0 : _b.on('change', () => {
                    // Because we've set bootstrap to localStorage, LaunchDarkly will
                    // attempt to fetch the flags from persistent storage in order to make
                    // them immediately available on page load. Meanwhile, after the ready
                    // event, the client makes a background request to LaunchDarkly to fetch
                    // the latest values and will subsequently store them in localStorage.
                    // We subscribe to the change event with a noop so that LaunchDarkly
                    // always syncs localStorage with the latest values behind the scenes
                    // while the application is running. Thus when the browser is reloaded
                    // it will always be bootstrapped with the most recent set of flags.
                });
            });
        }
    }
    /**
     * Warns about LaunchDarkly not being able to fetch the flags and against
     * discrepancies between the server flags and the default flags.
     */
    validateFlags() {
        if (this.client) {
            const flags = this.client.allFlags();
            if (!Object.keys(flags).length) {
                console.warn('There was an error connecting to LaunchDarkly. Falling back to default flags.', defaultFeatureFlags);
            }
            else {
                const missingDefaultFlags = Object.keys(flags).filter((flag) => typeof defaultFeatureFlags[flag] === 'undefined');
                const missingLaunchDarklyFlags = Object.keys(defaultFeatureFlags).filter((flag) => typeof flags[flag] === 'undefined');
                const isDevelopment = process.env.NODE_ENV === 'development';
                if (isDevelopment &&
                    (missingDefaultFlags.length || missingLaunchDarklyFlags.length)) {
                    console.warn(`There is a discrepancy between the LaunchDarkly and default feature flags.\n`, missingDefaultFlags.length
                        ? `- LaunchDarkly has flags without defaults:\n\t${missingDefaultFlags.join(',\n\t')}\n`
                        : '', missingLaunchDarklyFlags.length
                        ? `- The fallback has flags not in LaunchDarkly:\n\t${missingLaunchDarklyFlags.join(',\n\t')}`
                        : '');
                }
            }
        }
    }
}
export const featureFlags = new FeatureFlags();
